import {useCallback, useEffect, useMemo, useRef, useState, type CSSProperties} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import type {ProjectListItem} from '../../common/api/generated/model/projectListItem'
import type {ProjectListSort} from '../../common/api/generated/model/projectListSort'
import type {ProjectListPricing} from '../../common/api/generated/model/projectListPricing'
import {
  listProjectColors,
  listProjectCounts,
  listProjectTags,
  listProjects,
} from '../../common/api/generated/endpoints/projects/projects'
import {COLOR_PALETTE} from '../../common/constants/colorPalette'
import {getMe} from '../../common/api/generated/endpoints/auth/auth'
import {useAuth} from '../auth/useAuth'
import {useToast} from '../../common/components/toast/toastContext'
import {handleApiError} from '../../common/errors/handleApiError'
import {trackEvent} from '../../common/analytics/trackEvent'
import Icon from '../components/templates/Icon'
import TemplateCard from '../components/templates/TemplateCard'
import Pager from '../components/templates/Pager'
import ShareCta from '../components/templates/ShareCta'
import LoadingLogo from '../components/LoadingLogo'
import TagAutocomplete from '../components/sharedWidgets/TagAutocomplete'
import './TemplatesPage.css'

type TierFilter = 'all' | 'free' | 'premium'

const PAGE_SIZE = 12

const TemplatesPage = () => {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const {user} = useAuth()
  const toast = useToast()

  // Page of filtered projects shown in the grid
  const [projects, setProjects] = useState<ProjectListItem[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [retryNonce, setRetryNonce] = useState(0)
  const meIdRef = useRef<string | null>(null)

  // Filter state
  const [sort, setSort] = useState<ProjectListSort>('featured')
  const [tier, setTier] = useState<TierFilter>('all')
  const [colors, setColors] = useState<string[]>([])
  const [colorSuggestions, setColorSuggestions] = useState<string[]>([])
  const [tags, setTags] = useState<string[]>([])
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([])
  const [counts, setCounts] = useState({all: 0, free: 0, premium: 0})
  const [filtersOpen, setFiltersOpen] = useState(false)

  // Capture a single "now" timestamp at mount so card age math stays pure across re-renders.
  const [nowMs] = useState(() => Date.now())

  // Refresh distinct-tag / distinct-color lists from the server. Called on mount + after every
  // refetch so suggestions stay in sync with DB state. The user's active selection is NEVER pruned
  // here — pruning it mid-fetch cleared the just-clicked filter (and refired with no filter).
  const refreshTagSuggestions = useCallback(() => {
    listProjectTags()
      .then(({data}) => setTagSuggestions(data))
      .catch((err) => {
        handleApiError(err, 'templates.tagsList')
      })
  }, [])

  const refreshColorSuggestions = useCallback(() => {
    listProjectColors()
      .then(({data}) => setColorSuggestions(data))
      .catch((err) => {
        handleApiError(err, 'templates.colorsList')
      })
  }, [])

  // Fetch the current page whenever filters or page change (also runs on mount + retry).
  useEffect(() => {
    let cancelled = false
    const pricing: ProjectListPricing | undefined = tier === 'all' ? undefined : tier
    const run = async () => {
      setLoading(true)
      setError(false)
      try {
        const {data} = await listProjects({
          sort,
          ...(pricing && {pricing}),
          ...(tags.length > 0 && {tags}),
          ...(colors.length > 0 && {colors}),
          page,
          pageSize: PAGE_SIZE,
        })
        if (cancelled) return
        setProjects(data.items)
        setTotal(data.total)
        refreshTagSuggestions()
        refreshColorSuggestions()
      } catch (err) {
        if (cancelled) return
        toast.error(handleApiError(err, 'templates.list') ?? t('templates.error'))
        setError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [sort, tier, tags, colors, page, retryNonce, t, toast, refreshTagSuggestions, refreshColorSuggestions])

  // Readiness-aware tier counts (independent of the active filters), fetched once on mount + retry.
  useEffect(() => {
    listProjectCounts()
      .then(({data}) => setCounts(data))
      .catch((err) => {
        handleApiError(err, 'templates.counts')
      })
  }, [retryNonce])

  // Resolve current user's internal id (so we open own projects directly)
  useEffect(() => {
    if (!user) return
    getMe()
      .then(({data}) => {
        meIdRef.current = data.id
      })
      .catch(() => {})
  }, [user])

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Show palette swatches present on a public scene, unioned with the active selection so a
  // just-clicked color never vanishes from the row. Full palette before the first response lands.
  const visibleColors = useMemo(() => {
    const allowed = new Set([...colorSuggestions, ...colors])
    if (allowed.size === 0) return COLOR_PALETTE
    return COLOR_PALETTE.filter((c) => allowed.has(c.id))
  }, [colorSuggestions, colors])

  const toggleColor = useCallback((id: string) => {
    setColors((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
    setPage(1)
  }, [])

  const handleSortChange = useCallback((next: ProjectListSort) => {
    setSort(next)
    setPage(1)
    trackEvent('gallery_sort_changed', {sort: next})
  }, [])

  const handleTierChange = useCallback((next: TierFilter) => {
    setTier(next)
    setPage(1)
    trackEvent('gallery_tier_filter_changed', {tier: next})
  }, [])

  const handleTagsChange = useCallback((next: string[]) => {
    setTags(next)
    setPage(1)
  }, [])

  const handlePageChange = useCallback((next: number) => {
    setPage(next)
    window.scrollTo({top: 0, behavior: 'smooth'})
  }, [])

  const handleClick = useCallback(
    (project: ProjectListItem) => {
      trackEvent('gallery_item_opened', {
        project_id: project.id,
        tier: project.isFree ? 'free' : 'premium',
        signed_in: user !== null,
      })
      if (user) {
        if (project.author && meIdRef.current === project.author.id) {
          navigate(`/editor/${project.id}`)
        } else {
          navigate(`/editor/new/${project.slug}`)
        }
      } else if (project.isFree) {
        navigate(`/free?project=${project.slug}`)
      } else {
        navigate('/login')
      }
    },
    [navigate, user],
  )

  const filterCount = colors.length + tags.length

  return (
    <div className="templates-root">
      <div className="bg-grid" />
      <div className="noise" />

      <main className="projects-page">
        <header className="projects-head">
          <span className="projects-eyebrow">{t('templates.eyebrow')}</span>
          <h1 className="projects-title">
            {t('templates.titlePrefix')} <em>{t('templates.titleEm')}</em> {t('templates.titleSuffix')}
          </h1>
          <p className="projects-sub">{t('templates.subtitle')}</p>
          <ShareCta />
        </header>

        <div className="projects-toolbar">
          <div className="toolbar-group">
            <span className="toolbar-label">{t('templates.toolbar.sort')}</span>
            <button
              type="button"
              className={`tpl-chip ${sort === 'featured' ? 'active' : ''}`}
              onClick={() => handleSortChange('featured')}
            >
              <Icon name="star" size={13} /> {t('templates.toolbar.sortFeatured')}
            </button>
            <button
              type="button"
              className={`tpl-chip ${sort === 'newest' ? 'active' : ''}`}
              onClick={() => handleSortChange('newest')}
            >
              <Icon name="clock" size={13} /> {t('templates.toolbar.sortNewest')}
            </button>
            <button
              type="button"
              className={`tpl-chip ${sort === 'hottest' ? 'active' : ''}`}
              onClick={() => handleSortChange('hottest')}
            >
              <Icon name="flame" size={13} /> {t('templates.toolbar.sortHottest')}
            </button>
          </div>

          <div className="toolbar-group">
            <span className="toolbar-label">{t('templates.toolbar.show')}</span>
            <button
              type="button"
              className={`tpl-chip ${tier === 'all' ? 'active' : ''}`}
              onClick={() => handleTierChange('all')}
            >
              {t('templates.toolbar.showAll')} <span className="chip-count">{counts.all}</span>
            </button>
            <button
              type="button"
              className={`tpl-chip ${tier === 'free' ? 'active' : ''}`}
              onClick={() => handleTierChange('free')}
            >
              {t('templates.toolbar.showFree')} <span className="chip-count">{counts.free}</span>
            </button>
            <button
              type="button"
              className={`tpl-chip ${tier === 'premium' ? 'active' : ''}`}
              onClick={() => handleTierChange('premium')}
            >
              <Icon name="crown" size={12} /> {t('templates.toolbar.showPremium')}{' '}
              <span className="chip-count">{counts.premium}</span>
            </button>
          </div>

          <button
            type="button"
            className={`filters-toggle ${filtersOpen ? 'open' : ''} ${filterCount > 0 ? 'has-value' : ''}`}
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
          >
            <Icon name="filter" size={13} />
            <span>{t('templates.toolbar.filters')}</span>
            {filterCount > 0 && <span className="filters-toggle-count">{filterCount}</span>}
            <span className="filters-toggle-chev">
              <Icon name="chevron" size={13} />
            </span>
          </button>

          {filtersOpen && (
            <div className="toolbar-extras">
              <div className="toolbar-group">
                <span className="toolbar-label">{t('templates.toolbar.colors')}</span>
                <div className="color-swatches">
                  {visibleColors.map((c) => {
                    const isActive = colors.includes(c.id)
                    const swatchStyle = {'--c': c.hex} as CSSProperties
                    return (
                      <button
                        key={c.id}
                        type="button"
                        className={`color-swatch ${isActive ? 'active' : ''}`}
                        style={swatchStyle}
                        onClick={() => toggleColor(c.id)}
                        title={c.label}
                        aria-label={c.label}
                        aria-pressed={isActive}
                      >
                        <span className="color-check">
                          <Icon name="check" size={11} />
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>

              <div className="toolbar-group toolbar-group-tags">
                <span className="toolbar-label">{t('templates.toolbar.tags')}</span>
                <TagAutocomplete
                  value={tags}
                  onChange={handleTagsChange}
                  suggestions={tagSuggestions}
                  allowCreate={false}
                  placeholder={t('templates.toolbar.allTags')}
                  ariaLabel={t('templates.toolbar.tags')}
                />
              </div>
            </div>
          )}
        </div>

        {error && projects.length === 0 ? (
          <div className="tpl-empty">
            <p className="tpl-error">{t('templates.error')}</p>
            <button type="button" className="mp-empty-cta" onClick={() => setRetryNonce((n) => n + 1)}>
              {t('projects.retry')}
            </button>
          </div>
        ) : loading ? (
          <div className="tpl-loading">
            <LoadingLogo size={56} />
          </div>
        ) : projects.length === 0 ? (
          <div className="tpl-empty">{t('templates.empty')}</div>
        ) : (
          <>
            <div className="tpl-cards">
              {projects.map((p) => (
                <TemplateCard key={p.id} project={p} onClick={handleClick} nowMs={nowMs} />
              ))}
            </div>
            <Pager page={page} totalPages={totalPages} onChange={handlePageChange} />
          </>
        )}
      </main>
    </div>
  )
}

export default TemplatesPage
